import {
  createContext,
  useCallback,
  useContext,
  useMemo,
  useState,
  type ReactNode,
} from 'react'
import { sanitizeUserFacingMessage } from '../utils/userMessage'

type ShowDialogFn = (opts: { title: string; message: string }) => void

const AppDialogContext = createContext<ShowDialogFn | null>(null)

export function AppDialogProvider({ children }: { children: ReactNode }) {
  const [open, setOpen] = useState(false)
  const [title, setTitle] = useState('')
  const [message, setMessage] = useState('')

  const show = useCallback((opts: { title: string; message: string }) => {
    setTitle(opts.title)
    setMessage(sanitizeUserFacingMessage(opts.message))
    setOpen(true)
  }, [])

  const close = useCallback(() => setOpen(false), [])

  const value = useMemo(() => show, [show])

  return (
    <AppDialogContext.Provider value={value}>
      {children}
      {open ? (
        <div
          className="fixed inset-0 z-[220] flex items-end sm:items-center justify-center p-4 bg-black/50"
          role="dialog"
          aria-modal="true"
          aria-labelledby="app-dialog-title"
          onClick={(e) => {
            if (e.target === e.currentTarget) close()
          }}
        >
          <div
            className="w-full max-w-md rounded-t-2xl sm:rounded-2xl bg-white dark:bg-gray-900 shadow-xl border border-gray-200 dark:border-gray-700 p-5 sm:p-6"
            onClick={(e) => e.stopPropagation()}
          >
            <h2
              id="app-dialog-title"
              className="text-lg font-bold text-gray-900 dark:text-white pr-8"
            >
              {title}
            </h2>
            <p className="mt-3 text-sm text-gray-700 dark:text-gray-300 whitespace-pre-wrap break-words">
              {message}
            </p>
            <button
              type="button"
              onClick={close}
              className="mt-6 w-full py-3 rounded-xl font-semibold text-white bg-gray-900 dark:bg-indigo-600 hover:opacity-95 active:opacity-90 touch-manipulation"
            >
              Понятно
            </button>
          </div>
        </div>
      ) : null}
    </AppDialogContext.Provider>
  )
}

export function useAppDialog(): ShowDialogFn {
  const ctx = useContext(AppDialogContext)
  if (!ctx) {
    return (opts) => {
      const msg = sanitizeUserFacingMessage(opts.message)
      console.warn('[AppDialog] useAppDialog outside AppDialogProvider:', opts.title, msg)
    }
  }
  return ctx
}
